import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import ResponsiveShadow from './components/ResponsiveShadowLogin';
import CPFMaskInput from './components/masks/CPFMaskInput';
import CNPJMaskInput from './components/masks/CNPJMaskInput';
import { unformatCPF } from './utils/cpfUtils';

const styles = `
  @media (min-width: 768px) {
    .login-card-wrapper {
      margin-right: clamp(1rem, 6vw, 6rem);
    }
  }
`;

function ForgotPasswordPage() {
  const [step, setStep] = useState(1);
  const [cpf, setCpf] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [email, setEmail] = useState('');
  const [codigoExibido, setCodigoExibido] = useState('');
  const [emailMascarado, setEmailMascarado] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  function unformatCNPJ(value) {
    return value.replace(/[^\d]/g, '');
  }

  async function handleGerarCodigo(event) {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      const response = await axios.post(API_BASE_URL.FORGOT_PASSWORD, {
        cpf: unformatCPF(cpf),
        cnpj: unformatCNPJ(cnpj),
        email,
      });
      const { codigo, email: emailMask } = response.data;
      setCodigoExibido(codigo);
      setEmailMascarado(emailMask);
      setStep(2);
    } catch (error) {
      const msg = error.response?.data?.mensagem || 'Erro ao gerar código. Tente novamente.';
      setErrorMessage(msg);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleRedefinir(event) {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      await axios.post(API_BASE_URL.RESET_PASSWORD, {
        token,
        cpf: unformatCPF(cpf),
        email,
        newPassword,
        confirmPassword,
      });
      setSuccessMessage('Senha redefinida com sucesso!');
      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      const msg = error.response?.data?.mensagem || 'Erro ao redefinir senha. Tente novamente.';
      setErrorMessage(msg);
    } finally {
      setIsLoading(false);
    }
  }

  const inputIconStyle = { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d', zIndex: 1 };

  return (
    <>
      <style>{styles}</style>
      <div
        className="d-flex flex-column"
        style={{
          height: '100vh',
          width: '100vw',
          overflow: 'hidden',
          backgroundImage: 'url(/assets/background_login.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="d-flex" style={{ flex: '1', minHeight: 0 }}>
          <div className="d-none d-md-flex align-items-center justify-content-center" style={{ flex: '2' }}>
            <img
              src="/assets/logo_ac_planeja.png"
              alt="Logo"
              className="img-fluid"
              style={{ maxHeight: '70%', objectFit: 'contain' }}
            />
          </div>
          <div className="d-flex align-items-center justify-content-center justify-content-md-end px-4" style={{ flex: '1' }}>
            <div className="w-100 login-card-wrapper" style={{ maxWidth: '420px' }}>
              <ResponsiveShadow
                style={{
                  width: '100%',
                  maxHeight: '90vh',
                  backgroundColor: '#fff',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                }}
              >
                <div className="p-4" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                  {step === 1 ? (
                    <>
                      <h3 className="text-center mb-0">Esqueci a Senha</h3>
                      <div style={{ height: '3px', background: 'linear-gradient(90deg, #0b3383, #0e48ad)', margin: '0.5rem auto 1rem', width: '100%', borderRadius: '2px' }}></div>
                      <form onSubmit={handleGerarCodigo}>
                        <div className="mb-3">
                          <label htmlFor="cpf" className="form-label">CPF</label>
                          <div style={{ position: 'relative' }}>
                            <i className="bi bi-person-fill" style={inputIconStyle} />
                            <CPFMaskInput
                              id="cpf"
                              value={cpf}
                              onChange={e => setCpf(e.target.value)}
                              style={{ paddingLeft: '2.8rem' }}
                              required
                            />
                          </div>
                        </div>
                        <div className="mb-3">
                          <label htmlFor="cnpj" className="form-label">CNPJ</label>
                          <div style={{ position: 'relative' }}>
                            <i className="bi bi-building" style={inputIconStyle} />
                            <CNPJMaskInput
                              id="cnpj"
                              value={cnpj}
                              onChange={e => setCnpj(e.target.value)}
                              style={{ paddingLeft: '2.8rem' }}
                              required
                            />
                          </div>
                        </div>
                        <div className="mb-3">
                          <label htmlFor="email" className="form-label">Email</label>
                          <div style={{ position: 'relative' }}>
                            <i className="bi bi-envelope-fill" style={inputIconStyle} />
                            <input
                              type="email"
                              className="form-control"
                              id="email"
                              value={email}
                              onChange={e => setEmail(e.target.value)}
                              style={{ paddingLeft: '2.8rem' }}
                              required
                            />
                          </div>
                        </div>
                        {errorMessage && (
                          <div className="alert alert-danger py-2" role="alert">{errorMessage}</div>
                        )}
                        <button type="submit" className="btn btn-primary w-100 mt-2" disabled={isLoading}>
                          {isLoading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                              Gerando...
                            </>
                          ) : 'Gerar código'}
                        </button>
                      </form>
                      <div className="text-center mt-3">
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          onClick={() => navigate('/')}
                          style={{ textDecoration: 'none', fontSize: '0.875rem' }}
                        >
                          <i className="bi bi-arrow-left me-1"></i>
                          Voltar para o login
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <h3 className="text-center mb-0">Redefinir Senha</h3>
                      <div style={{ height: '3px', background: 'linear-gradient(90deg, #0b3383, #0e48ad)', margin: '0.5rem auto 1rem', width: '100%', borderRadius: '2px' }}></div>
                      {codigoExibido && (
                        <div className="alert alert-success text-center py-2 mb-3" role="alert">
                          <p className="mb-1 small">Seu código de segurança:</p>
                          <div
                            style={{
                              fontSize: '1.8rem',
                              fontWeight: 'bold',
                              letterSpacing: '8px',
                              fontFamily: 'monospace',
                              color: '#155724',
                            }}
                          >
                            {codigoExibido}
                          </div>
                          {emailMascarado && (
                            <p className="mt-1 mb-0 small">Código válido para o email {emailMascarado}</p>
                          )}
                        </div>
                      )}
                      <form onSubmit={handleRedefinir}>
                        <div className="mb-3">
                          <label htmlFor="token" className="form-label">Código</label>
                          <div style={{ position: 'relative' }}>
                            <i className="bi bi-key-fill" style={inputIconStyle} />
                            <input
                              type="text"
                              className="form-control"
                              id="token"
                              value={token}
                              onChange={e => setToken(e.target.value)}
                              placeholder="Digite o código de 6 dígitos"
                              maxLength={6}
                              required
                              style={{ paddingLeft: '2.8rem', fontFamily: 'monospace', fontSize: '1.2rem', letterSpacing: '4px' }}
                            />
                          </div>
                        </div>
                        <div className="mb-3">
                          <label htmlFor="newPassword" className="form-label">Nova senha</label>
                          <div style={{ position: 'relative' }}>
                            <i className="bi bi-lock-fill" style={inputIconStyle} />
                            <input
                              type={showPassword ? 'text' : 'password'}
                              className="form-control"
                              id="newPassword"
                              value={newPassword}
                              onChange={e => setNewPassword(e.target.value)}
                              required
                              style={{ paddingLeft: '2.8rem', paddingRight: '2.8rem' }}
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              tabIndex={-1}
                              aria-label={showPassword ? 'Esconder senha' : 'Mostrar senha'}
                              style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#6c757d', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px', borderRadius: '4px', transition: 'color 0.2s' }}
                              onMouseEnter={e => { e.target.style.color = '#212529'; }}
                              onMouseLeave={e => { e.target.style.color = '#6c757d'; }}
                            >
                              {showPassword ? <i className="bi bi-eye-slash-fill" /> : <i className="bi bi-eye-fill" />}
                            </button>
                          </div>
                        </div>
                        <div className="mb-3">
                          <label htmlFor="confirmPassword" className="form-label">Confirmar senha</label>
                          <div style={{ position: 'relative' }}>
                            <i className="bi bi-lock-fill" style={inputIconStyle} />
                            <input
                              type={showConfirmPassword ? 'text' : 'password'}
                              className="form-control"
                              id="confirmPassword"
                              value={confirmPassword}
                              onChange={e => setConfirmPassword(e.target.value)}
                              required
                              style={{ paddingLeft: '2.8rem', paddingRight: '2.8rem' }}
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              tabIndex={-1}
                              aria-label={showConfirmPassword ? 'Esconder senha' : 'Mostrar senha'}
                              style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#6c757d', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px', borderRadius: '4px', transition: 'color 0.2s' }}
                              onMouseEnter={e => { e.target.style.color = '#212529'; }}
                              onMouseLeave={e => { e.target.style.color = '#6c757d'; }}
                            >
                              {showConfirmPassword ? <i className="bi bi-eye-slash-fill" /> : <i className="bi bi-eye-fill" />}
                            </button>
                          </div>
                        </div>
                        {errorMessage && (
                          <div className="alert alert-danger py-2" role="alert">{errorMessage}</div>
                        )}
                        {successMessage && (
                          <div className="alert alert-success py-2" role="alert">{successMessage}</div>
                        )}
                        <button type="submit" className="btn btn-primary w-100 mt-2" disabled={isLoading}>
                          {isLoading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                              Redefinindo...
                            </>
                          ) : 'Redefinir senha'}
                        </button>
                      </form>
                      <div className="text-center mt-3">
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          onClick={() => {
                            setStep(1);
                            setErrorMessage('');
                            setSuccessMessage('');
                          }}
                          style={{ textDecoration: 'none', fontSize: '0.875rem' }}
                        >
                          <i className="bi bi-arrow-left me-1"></i>
                          Gerar novo código
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </ResponsiveShadow>
            </div>
          </div>
        </div>

        <footer style={{ borderTop: '1px solid rgba(255,255,255,0.4)', background: 'transparent', padding: '0.5rem 0', flexShrink: 0 }}>
          <div className="container text-white text-center" style={{ fontSize: '0.75rem' }}>
            <div className="d-flex justify-content-center align-items-center gap-2 flex-wrap">
              <span><i className="bi bi-whatsapp me-1"></i><a href="https://wa.me/5581982621289" target="_blank" rel="noopener noreferrer" className="text-white text-decoration-none">(81) 98262-1289</a></span>
              <span className="text-white-50">|</span>
              <span><i className="bi bi-envelope-fill me-1"></i><a href="mailto:suporte.sistemas@acpublica.com.br" className="text-white text-decoration-none">suporte.sistemas@acpublica.com.br</a></span>
              <span className="text-white-50">|</span>
              <span><i className="bi bi-globe me-1"></i><a href="https://www.acpublica.com.br" target="_blank" rel="noopener noreferrer" className="text-white text-decoration-none">www.acpublica.com.br</a></span>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

export default ForgotPasswordPage;
